import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { searchProducts } from "../api";
import { useCurrency } from "../../Context/CurrencyContext";
import "./SearchResultsPage.css";

const API_BASE_URL = "https://volumaire.backendapihub.com";
const DEFAULT_IMAGE = "/images/image_not_found 1.svg";
const FALLBACK_IMAGE = "https://via.placeholder.com/300x300?text=No+Image";

/* ─── Icons ──────────────────────────────────────────────────────────────── */
const FilterIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <line x1="4" y1="21" x2="4" y2="14" /><line x1="4" y1="10" x2="4" y2="3" />
    <line x1="12" y1="21" x2="12" y2="12" /><line x1="12" y1="8" x2="12" y2="3" />
    <line x1="20" y1="21" x2="20" y2="16" /><line x1="20" y1="12" x2="20" y2="3" />
    <line x1="1" y1="14" x2="7" y2="14" /><line x1="9" y1="8" x2="15" y2="8" />
    <line x1="17" y1="16" x2="23" y2="16" />
  </svg>
);

const ChevronDown = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);
const ChevronUp = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
    <polyline points="18 15 12 9 6 15" />
  </svg>
);

/* ─── Sort Dropdown ──────────────────────────────────────────────────────── */
const SortDropdown = ({ sortBy, onSortChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const opts = [
    { value: "relevance", label: "Relevance" },
    { value: "featured", label: "Featured" },
    { value: "bestSelling", label: "Best selling" },
    { value: "alphaAZ", label: "Alphabetically, A-Z" },
    { value: "alphaZA", label: "Alphabetically, Z-A" },
    { value: "priceLowHigh", label: "Price, low to high" },
    { value: "priceHighLow", label: "Price, high to low" },
    { value: "dateOldNew", label: "Date, old to new" },
    { value: "dateNewOld", label: "Date, new to old" },
  ];

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const label = opts.find((o) => o.value === sortBy)?.label || "Relevance";

  return (
    <div className="srp-sort" ref={ref}>
      <button className="srp-sort-trigger" onClick={() => setOpen(!open)}>
        <span className="srp-sort-prefix">Sort by:</span>
        <span className="srp-sort-val">&nbsp;{label}</span>
      </button>
      {open && (
        <div className="srp-sort-dropdown">
          {opts.map((o) => (
            <div
              key={o.value}
              className={`srp-sort-opt ${sortBy === o.value ? "srp-sort-opt--active" : ""}`}
              onClick={() => { onSortChange(o.value); setOpen(false); }}
            >
              {o.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ─── Filter Drawer ──────────────────────────────────────────────────────── */
const FilterDrawer = ({ open, onClose, filters, setFilters, onApply, onReset, productCounts }) => {
  const [expanded, setExpanded] = useState({ brand: true, productType: false, availability: false });
  const toggle = (k) => setExpanded((p) => ({ ...p, [k]: !p[k] }));

  const check = (cat, val) =>
    setFilters((prev) => ({
      ...prev,
      [cat]: prev[cat].includes(val) ? prev[cat].filter((i) => i !== val) : [...prev[cat], val],
    }));

  const brands = Object.keys(productCounts?.brands || {}).filter(Boolean);
  const types = Object.keys(productCounts?.types || {}).filter(Boolean);

  return (
    <>
      {open && <div className="srp-overlay" onClick={onClose} />}
      <div className={`srp-drawer ${open ? "srp-drawer--open" : ""}`}>
        <div className="srp-drawer-head">
          <span className="srp-drawer-title">Filter</span>
          <button className="srp-drawer-x" onClick={onClose}>✕</button>
        </div>
        <div className="srp-drawer-body">
          <div className="srp-fsec">
            <button className="srp-fsec-title" onClick={() => toggle("brand")}>
              <span>Brand</span>
              {expanded.brand ? <ChevronUp /> : <ChevronDown />}
            </button>
            {expanded.brand && (
              <div className="srp-fsec-opts">
                {(brands.length > 0 ? brands : ["MOKOSH", "BKIND"]).map((b) => (
                  <label key={b} className="srp-flabel">
                    <input type="checkbox" checked={filters.brands.includes(b)} onChange={() => check("brands", b)} />
                    <span>{b} ({productCounts?.brands?.[b] || 0})</span>
                  </label>
                ))}
              </div>
            )}
          </div>
          <div className="srp-fsec">
            <button className="srp-fsec-title" onClick={() => toggle("productType")}>
              <span>Product type</span>
              {expanded.productType ? <ChevronUp /> : <ChevronDown />}
            </button>
            {expanded.productType && (
              <div className="srp-fsec-opts">
                {(types.length > 0 ? types : ["Balm", "Cream", "Face Care", "Gel", "Scrub", "Serum"]).map((t) => (
                  <label key={t} className="srp-flabel">
                    <input type="checkbox" checked={filters.types.includes(t)} onChange={() => check("types", t)} />
                    <span>{t} ({productCounts?.types?.[t] || 0})</span>
                  </label>
                ))}
              </div>
            )}
          </div>
          <div className="srp-fsec">
            <button className="srp-fsec-title" onClick={() => toggle("availability")}>
              <span>Availability</span>
              {expanded.availability ? <ChevronUp /> : <ChevronDown />}
            </button>
            {expanded.availability && (
              <div className="srp-fsec-opts">
                <label className="srp-flabel">
                  <input type="checkbox" checked={filters.availability.includes("inStock")} onChange={() => check("availability", "inStock")} />
                  <span>In stock ({productCounts?.availability?.inStock || 0})</span>
                </label>
                <label className="srp-flabel">
                  <input type="checkbox" checked={filters.availability.includes("outOfStock")} onChange={() => check("availability", "outOfStock")} />
                  <span>Out of stock ({productCounts?.availability?.outOfStock || 0})</span>
                </label>
              </div>
            )}
          </div>
        </div>
        <div className="srp-drawer-foot">
          <button className="srp-apply-btn" onClick={onApply}>APPLY</button>
          <button className="srp-reset-btn" onClick={onReset}>REMOVE ALL</button>
        </div>
      </div>
    </>
  );
};

/* ─── Article Card ───────────────────────────────────────────────────────── */
const ArticleCard = ({ article, onClick }) => (
  <div className="srp-article-card" onClick={onClick}>
    {article.image && (
      <div className="srp-article-img-wrap">
        <img
          src={article.image}
          alt={article.title}
          className="srp-article-img"
          onError={(e) => { e.target.onerror = null; e.target.src = FALLBACK_IMAGE; }}
          loading="lazy"
        />
      </div>
    )}
    <div className="srp-article-body">
      {article.category && <span className="srp-article-cat">{article.category}</span>}
      <h3 className="srp-article-title">{article.title}</h3>
      {article.excerpt && <p className="srp-article-excerpt">{article.excerpt}</p>}
      {article.date && <span className="srp-article-date">{article.date}</span>}
      <span className="srp-article-link">Read more →</span>
    </div>
  </div>
);

/* ─── Page Card ──────────────────────────────────────────────────────────── */
const PageCard = ({ page, onClick }) => (
  <div className="srp-page-card" onClick={onClick}>
    <div className="srp-page-icon">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
        <polyline points="10 9 9 9 8 9" />
      </svg>
    </div>
    <div className="srp-page-body">
      <h3 className="srp-page-title">{page.title}</h3>
      {page.description && <p className="srp-page-desc">{page.description}</p>}
    </div>
    <span className="srp-page-arrow">→</span>
  </div>
);

/* ─── Main ───────────────────────────────────────────────────────────────── */
const SearchResultsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { formatPrice } = useCurrency();

  const initialQuery =
    location.state?.query ||
    new URLSearchParams(location.search).get("q") ||
    "";

  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [articles, setArticles] = useState([]);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [articlesLoading, setArticlesLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState("relevance");
  const [filterOpen, setFilterOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("products");
  const [filters, setFilters] = useState({ brands: [], types: [], availability: [] });
  const [appliedFilters, setAppliedFilters] = useState({ brands: [], types: [], availability: [] });

  /* ── helpers ── */
  const extractPrice = (p) => {
    if (!p) return 0;
    for (const f of ["price", "salePrice", "regularPrice", "productPrice", "discountedPrice", "offerPrice", "finalPrice"]) {
      const v = p[f];
      if (v != null) {
        if (typeof v === "number") return v;
        if (typeof v === "string") { const n = parseFloat(v.replace(/[$,€£¥\s]/g, "")); if (!isNaN(n)) return n; }
      }
    }
    return 0;
  };

  const extractOriginalPrice = (p) => {
    for (const f of ["originalPrice", "regularPrice", "compareAtPrice", "oldPrice", "listPrice"]) {
      const v = p?.[f];
      if (v != null) {
        if (typeof v === "number") return v;
        if (typeof v === "string") { const n = parseFloat(v.replace(/[$,€£¥\s]/g, "")); if (!isNaN(n)) return n; }
      }
    }
    return null;
  };

  const extractImageUrl = (p) => {
    if (!p) return DEFAULT_IMAGE;
    const abs = (url) => {
      if (!url || typeof url !== "string") return null;
      if (url.startsWith("http://") || url.startsWith("https://")) return url;
      if (url.startsWith("//")) return `https:${url}`;
      if (url.startsWith("/")) return `${API_BASE_URL}${url}`;
      return `${API_BASE_URL}/${url}`;
    };
    for (const f of ["image", "img", "picture", "photo", "mainImage", "featuredImage", "thumbnail", "imageUrl", "imgUrl", "productImage"]) {
      if (p[f] && typeof p[f] === "string") { const u = abs(p[f]); if (u) return u; }
    }
    for (const f of ["images", "media", "gallery", "photos"]) {
      const arr = p[f];
      if (Array.isArray(arr) && arr.length > 0) {
        for (const item of arr) {
          if (typeof item === "string") { const u = abs(item); if (u) return u; }
          if (item && typeof item === "object") {
            for (const k of ["url", "src", "image", "img"]) { if (item[k]) { const u = abs(item[k]); if (u) return u; } }
          }
        }
      }
    }
    return DEFAULT_IMAGE;
  };

  const normalize = (arr) => {
    if (!Array.isArray(arr)) return [];
    return arr.map((p) => {
      const price = extractPrice(p);
      const originalPrice = extractOriginalPrice(p);
      const discountPercent = originalPrice && originalPrice > price
        ? Math.round(((originalPrice - price) / originalPrice) * 100) : null;
      return {
        _id: p._id || p.id,
        name: p.name || p.productName || "",
        image: extractImageUrl(p),
        price, originalPrice, discountPercent,
        brand: p.brand || "MOKOSH",
        type: p.type || p.category?.name || "Product",
        inStock: p.inStock !== undefined ? p.inStock : true,
        isBestseller: p.isBestseller || false,
      };
    });
  };

  const parseResponse = (res) => {
    if (!res) return [];
    if (res.success && res.data?.products) return res.data.products;
    if (res.success && Array.isArray(res.data)) return res.data;
    if (Array.isArray(res)) return res;
    if (res.data?.products) return res.data.products;
    if (Array.isArray(res.data)) return res.data;
    if (res.products) return res.products;
    return [];
  };

  /* ─── Parse Articles from API response ───────────────────────────────── */
  const parseArticles = (res) => {
    if (!res) return [];
    // Common API response shapes for articles/blogs
    const raw =
      res.data?.articles ||
      res.data?.blogs ||
      res.data?.posts ||
      res.articles ||
      res.blogs ||
      res.posts ||
      (Array.isArray(res.data) ? res.data : null) ||
      (Array.isArray(res) ? res : []);

    if (!Array.isArray(raw)) return [];

    return raw.map((a) => ({
      _id: a._id || a.id || Math.random().toString(36),
      title: a.title || a.name || a.heading || "",
      excerpt: a.excerpt || a.summary || a.description || a.body?.substring(0, 120) || "",
      image: extractImageUrl(a),
      category: a.category?.name || a.category || a.tag || a.type || "",
      date: a.publishedAt || a.createdAt || a.date
        ? new Date(a.publishedAt || a.createdAt || a.date).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })
        : "",
      slug: a.slug || a._id || a.id,
      relatedProducts: a.relatedProducts || a.products || [],
    }));
  };

  /* ─── Parse Pages from API response ──────────────────────────────────── */
  const parsePages = (res) => {
    if (!res) return [];
    const raw =
      res.data?.pages ||
      res.pages ||
      (Array.isArray(res.data) ? res.data : null) ||
      (Array.isArray(res) ? res : []);

    if (!Array.isArray(raw)) return [];

    return raw.map((p) => ({
      _id: p._id || p.id || Math.random().toString(36),
      title: p.title || p.name || "",
      description: p.description || p.excerpt || p.summary || "",
      slug: p.slug || p._id || p.id,
    }));
  };

  /* ── fetch products ── */
  useEffect(() => {
    if (!initialQuery) { setLoading(false); return; }
    (async () => {
      setLoading(true);
      try {
        const res = await searchProducts(initialQuery, { skipCache: false });
        const norm = normalize(parseResponse(res));
        setProducts(norm);
        setFilteredProducts(norm);

        // ── Try to extract articles & pages from same search response ──
        // Many backends return all content types in one search call
        const articlesFromRes = parseArticles(res);
        const pagesFromRes = parsePages(res);
        setArticles(articlesFromRes);
        setPages(pagesFromRes);
        setError(null);
      } catch {
        setError("Failed to load search results.");
        setProducts([]);
        setFilteredProducts([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [initialQuery]);

  /* ── fetch articles & pages separately if not in product response ── */
  useEffect(() => {
    if (!initialQuery || articles.length > 0) return;

    (async () => {
      setArticlesLoading(true);
      try {
        // Try dedicated articles endpoint
        const articlesRes = await fetch(
          `${API_BASE_URL}/api/articles/search?q=${encodeURIComponent(initialQuery)}`
        ).then((r) => r.json()).catch(() => null);

        if (articlesRes) {
          const parsedArticles = parseArticles(articlesRes);
          setArticles(parsedArticles);
        }

        // Try dedicated pages endpoint
        const pagesRes = await fetch(
          `${API_BASE_URL}/api/pages/search?q=${encodeURIComponent(initialQuery)}`
        ).then((r) => r.json()).catch(() => null);

        if (pagesRes) {
          const parsedPages = parsePages(pagesRes);
          setPages(parsedPages);
        }
      } catch {
        // silently fail — articles/pages not critical
      } finally {
        setArticlesLoading(false);
      }
    })();
  }, [initialQuery, articles.length]);

  /* ── counts ── */
  const productCounts = useMemo(() => {
    const c = { brands: {}, types: {}, availability: { inStock: 0, outOfStock: 0 } };
    products.forEach((p) => {
      if (p.brand) c.brands[p.brand] = (c.brands[p.brand] || 0) + 1;
      if (p.type) c.types[p.type] = (c.types[p.type] || 0) + 1;
      if (p.inStock) c.availability.inStock++;
      else c.availability.outOfStock++;
    });
    return c;
  }, [products]);

  /* ── filter + sort ── */
  useEffect(() => {
    let r = [...products];
    if (appliedFilters.brands.length) r = r.filter((p) => appliedFilters.brands.includes(p.brand));
    if (appliedFilters.types.length) r = r.filter((p) => appliedFilters.types.includes(p.type));
    if (appliedFilters.availability.length === 1) {
      const want = appliedFilters.availability[0] === "inStock";
      r = r.filter((p) => p.inStock === want);
    }
    if (sortBy === "alphaAZ") r.sort((a, b) => a.name.localeCompare(b.name));
    else if (sortBy === "alphaZA") r.sort((a, b) => b.name.localeCompare(a.name));
    else if (sortBy === "priceLowHigh") r.sort((a, b) => a.price - b.price);
    else if (sortBy === "priceHighLow") r.sort((a, b) => b.price - a.price);
    else if (sortBy === "bestSelling") r.sort((a, b) => (b.isBestseller ? 1 : 0) - (a.isBestseller ? 1 : 0));
    setFilteredProducts(r);
  }, [products, appliedFilters, sortBy]);

  /* ── Tab counts ── */
  const tabCounts = {
    products: filteredProducts.length,
    articles: articles.length,
    pages: pages.length,
  };

  const handleApply = () => { setAppliedFilters({ ...filters }); setFilterOpen(false); };
  const handleReset = () => {
    const r = { brands: [], types: [], availability: [] };
    setFilters(r); setAppliedFilters(r);
  };
  const handleImgErr = (e) => {
    e.target.onerror = null;
    e.target.src = e.target.src.includes(DEFAULT_IMAGE) ? FALLBACK_IMAGE : DEFAULT_IMAGE;
  };

  /* ── render ── */
  return (
    <div className="srp-page">

      {/* Heading */}
      <div className="srp-hero">
        <h1 className="srp-title">
          Search results:&nbsp;<span className="srp-title-q">{initialQuery}</span>
        </h1>
      </div>

      {/* Tabs */}
      <div className="srp-tabs-wrap">
        <div className="srp-tabs">
          {["products", "articles", "pages"].map((tab) => (
            <button
              key={tab}
              className={`srp-tab ${activeTab === tab ? "srp-tab--active" : ""}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab.toUpperCase()}
              {!loading && tabCounts[tab] > 0 && (
                <span className="srp-tab-count">{tabCounts[tab]}</span>
              )}
            </button>
          ))}
        </div>
        <div className="srp-tabs-line" />
      </div>

      {/* ══════════════ PRODUCTS TAB ══════════════ */}
      {activeTab === "products" && (
        <>
          {/* Toolbar */}
          {!loading && !error && (
            <div className="srp-toolbar">
              <span className="srp-count">
                {filteredProducts.length} result{filteredProducts.length !== 1 ? "s" : ""}
              </span>
              <div className="srp-toolbar-r">
                <button className="srp-filter-btn" onClick={() => setFilterOpen(true)}>
                  <FilterIcon />
                  <span>Filter</span>
                </button>
                <SortDropdown sortBy={sortBy} onSortChange={setSortBy} />
              </div>
            </div>
          )}

          {/* Filter Drawer */}
          <FilterDrawer
            open={filterOpen}
            onClose={() => setFilterOpen(false)}
            filters={filters}
            setFilters={setFilters}
            onApply={handleApply}
            onReset={handleReset}
            productCounts={productCounts}
          />

          {/* Loading */}
          {loading && (
            <div className="srp-state">
              <div className="srp-spinner" />
              <p>Searching products...</p>
            </div>
          )}

          {/* Error */}
          {error && !loading && (
            <div className="srp-state">
              <p className="srp-err-txt">{error}</p>
              <button className="srp-retry" onClick={() => window.location.reload()}>Try Again</button>
            </div>
          )}

          {/* Empty */}
          {!loading && !error && filteredProducts.length === 0 && (
            <div className="srp-state">
              <p>No products found for "{initialQuery}".</p>
            </div>
          )}

          {/* Grid */}
          {!loading && !error && filteredProducts.length > 0 && (
            <div className="srp-grid-wrap">
              <div className="srp-grid">
                {filteredProducts.map((item) => (
                  <div
                    key={item._id}
                    className="srp-card"
                    onClick={() => navigate(`/product/${item._id}`)}
                  >
                    <div className="srp-card-img-wrap">
                      <img
                        className="srp-card-img"
                        src={item.image}
                        alt={item.name}
                        onError={handleImgErr}
                        loading="lazy"
                      />
                      {!item.inStock && (
                        <span className="srp-badge srp-badge--oos">Out of Stock</span>
                      )}
                      {item.isBestseller && !item.discountPercent && (
                        <span className="srp-badge srp-badge--bs">Bestseller</span>
                      )}
                      {item.discountPercent && (
                        <span className="srp-badge srp-badge--disc">-{item.discountPercent}%</span>
                      )}
                    </div>
                    <p className="srp-card-brand">{item.brand}</p>
                    <p className="srp-card-name">{item.name}</p>
                    <div className="srp-card-prices">
                      <span className="srp-card-price">{formatPrice(item.price)}</span>
                      {item.originalPrice && item.originalPrice > item.price && (
                        <>
                          <span className="srp-card-old">{formatPrice(item.originalPrice)}</span>
                          {item.discountPercent && (
                            <span className="srp-card-pct">-{item.discountPercent}%</span>
                          )}
                        </>
                      )}
                    </div>
                    {!item.inStock && <div className="srp-card-oos-tag">Out of Stock</div>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* ══════════════ ARTICLES TAB ══════════════ */}
      {activeTab === "articles" && (
        <div className="srp-content-wrap">
          {/* Loading */}
          {(loading || articlesLoading) && (
            <div className="srp-state">
              <div className="srp-spinner" />
              <p>Searching articles...</p>
            </div>
          )}

          {/* Count */}
          {!loading && !articlesLoading && articles.length > 0 && (
            <div className="srp-toolbar">
              <span className="srp-count">
                {articles.length} article{articles.length !== 1 ? "s" : ""}
              </span>
            </div>
          )}

          {/* Empty */}
          {!loading && !articlesLoading && articles.length === 0 && (
            <div className="srp-state">
              <div className="srp-empty-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="1.2">
                  <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                  <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                </svg>
              </div>
              <p>No articles found for "{initialQuery}".</p>
            </div>
          )}

          {/* Articles grid */}
          {!loading && !articlesLoading && articles.length > 0 && (
            <div className="srp-articles-grid">
              {articles.map((article) => (
                <ArticleCard
                  key={article._id}
                  article={article}
                  onClick={() => navigate(`/blogs/${article.slug || article._id}`)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* ══════════════ PAGES TAB ══════════════ */}
      {activeTab === "pages" && (
        <div className="srp-content-wrap">
          {/* Loading */}
          {(loading || articlesLoading) && (
            <div className="srp-state">
              <div className="srp-spinner" />
              <p>Searching pages...</p>
            </div>
          )}

          {/* Count */}
          {!loading && !articlesLoading && pages.length > 0 && (
            <div className="srp-toolbar">
              <span className="srp-count">
                {pages.length} page{pages.length !== 1 ? "s" : ""}
              </span>
            </div>
          )}

          {/* Empty */}
          {!loading && !articlesLoading && pages.length === 0 && (
            <div className="srp-state">
              <div className="srp-empty-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="1.2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                </svg>
              </div>
              <p>No pages found for "{initialQuery}".</p>
            </div>
          )}

          {/* Pages list */}
          {!loading && !articlesLoading && pages.length > 0 && (
            <div className="srp-pages-list">
              {pages.map((page) => (
                <PageCard
                  key={page._id}
                  page={page}
                  onClick={() => navigate(`/pages/${page.slug || page._id}`)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchResultsPage;